import LinkButton from '@sats-group/ui-lib/react/link-button';
import Text from '@sats-group/ui-lib/react/text';
import cn from 'classnames';
import React from 'react';
import { useVideoControls } from '~/client/hooks/use-video-controls';
import { MediaType } from '~/shared/media-type-helper';
import BackgroundImage from '../background-image/background-image';
import BackgroundVideo from '../background-video/background-video';
import Tagline from '../tagline/tagline';
import { taglineThemes } from '../tagline/tagline.types';
import VideoTrigger from '../video-trigger/video-trigger';
import type { FrontpageHero as Props } from './frontpage-hero.types';

const FrontpageHero: React.FunctionComponent<Props> = ({
  title,
  tagline,
  taglineLabel,
  bannerSize,
  description,
  links,
  media,
  pause,
  play,
  disclaimer,
}) => {
  const hasVideo = media?.type === MediaType.Video;

  const {
    videoRef,
    isPlaying,
    play: playVideo,
    pause: pauseVideo,
    onPause,
    onTimeUpdate,
  } = useVideoControls();

  return (
    <section className={cn('frontpage-hero', `frontpage-hero--${bannerSize}`)}>
      <div
        className={cn(
          'frontpage-hero__inner',
          `frontpage-hero__inner--${bannerSize}`
        )}
      >
        <div className="frontpage-hero__content">
          <div
            className={cn(
              'frontpage-hero__content-inner',
              `frontpage-hero__content-inner--${bannerSize}`
            )}
          >
            <div className="frontpage-hero__text">
              {tagline ? (
                <Tagline
                  {...tagline}
                  theme={
                    taglineLabel
                      ? taglineThemes[
                          taglineLabel as keyof typeof taglineThemes
                        ]
                      : undefined
                  }
                  italic
                />
              ) : null}
              <Text
                elementName="h1"
                size={Text.sizes.headline1}
                theme={Text.themes.headline}
                italic
              >
                {title}
              </Text>

              {description ? (
                <Text
                  elementName="p"
                  size={Text.sizes.large}
                  theme={Text.themes.emphasis}
                >
                  {description}
                </Text>
              ) : null}
            </div>
            {links?.length > 0 ? (
              <div className="frontpage-hero__actions">
                {links.map((link, index) => (
                  <div className="frontpage-hero__action" key={link.href}>
                    <LinkButton
                      {...link}
                      size={LinkButton.sizes.large}
                      variant={
                        index
                          ? LinkButton.variants.ctaSecondaryWhite
                          : LinkButton.variants.cta
                      }
                      wide
                    />
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          {disclaimer ? (
            <Text
              elementName="p"
              size={Text.sizes.interface}
              className="frontpage-hero__disclaimer"
            >
              {disclaimer}
            </Text>
          ) : null}
        </div>
        <div className="frontpage-hero__media">
          {media?.type === MediaType.Image ? (
            <BackgroundImage
              {...media.props}
              focusArea={BackgroundImage.focusAreas.Center}
              className="frontpage-hero__image"
            />
          ) : null}

          {media?.type === MediaType.Video ? (
            <BackgroundVideo
              {...media.props}
              onPause={onPause}
              onTimeUpdate={onTimeUpdate}
              ref={videoRef}
            />
          ) : null}
        </div>
        {hasVideo ? (
          <div className="frontpage-hero__action-trigger">
            <VideoTrigger
              isPlaying={isPlaying}
              play={playVideo}
              pause={pauseVideo}
              playLabel={play}
              pauseLabel={pause}
            />
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default FrontpageHero;
